import * as pako from "pako";

export interface SavedCardGeometry {
  voxelCount: number;
  firstSliceIndex: number;
  lastSliceIndex: number;
  longAxisMm?: number;
  shortAxisMm?: number;
  /** Full segment in backend storage order: row + rows * (column + columns * slice). */
  volume?: {
    rows: number;
    columns: number;
    affine: number[];
    runs: [number, number][];
  };
  representative?: {
    sliceIndex: number;
    rows: number;
    columns: number;
    /** Row-major sparse runs on the representative mask plane. */
    runs: [number, number][];
    /** Three row-major affine rows mapping (column, row, slice, 1) to DICOM world. */
    affine?: number[];
  };
}

export function pixelIndicesToRuns(indices: readonly number[]): [number, number][] {
  const sorted = [...new Set(indices)].sort((a, b) => a - b);
  const runs: [number, number][] = [];
  for (const index of sorted) {
    const previous = runs.at(-1);
    if (previous && previous[0] + previous[1] === index) previous[1]++;
    else runs.push([index, 1]);
  }
  return runs;
}

type Bounds = {
  count: number;
  minRow: number;
  maxRow: number;
  minColumn: number;
  maxColumn: number;
};

function addPixel(bounds: Bounds | undefined, row: number, column: number): Bounds {
  if (!bounds) return { count: 1, minRow: row, maxRow: row, minColumn: column, maxColumn: column };
  bounds.count++;
  bounds.minRow = Math.min(bounds.minRow, row);
  bounds.maxRow = Math.max(bounds.maxRow, row);
  bounds.minColumn = Math.min(bounds.minColumn, column);
  bounds.maxColumn = Math.max(bounds.maxColumn, column);
  return bounds;
}

function extent(bounds: Bounds | undefined, spacing?: readonly number[]) {
  if (
    !bounds ||
    !spacing ||
    !spacing.slice(0, 2).every((value) => Number.isFinite(value) && value > 0)
  )
    return {};
  const height = (bounds.maxRow - bounds.minRow + 1) * spacing[0];
  const width = (bounds.maxColumn - bounds.minColumn + 1) * spacing[1];
  return { longAxisMm: Math.max(height, width), shortAxisMm: Math.min(height, width) };
}

/** Cache the exact submitted mask summary until the persisted mask is reloaded. */
export function summarizeLabelmap(
  labelmap: Record<string, number[][]>,
  spacing?: readonly number[],
  grid?: {
    rows: number;
    columns: number;
    origin?: readonly number[];
    direction?: readonly number[];
  },
): SavedCardGeometry | undefined {
  const slices = Object.entries(labelmap).filter(([, pixels]) => pixels.length > 0);
  if (!slices.length) return undefined;
  let largest: Bounds | undefined;
  let voxelCount = 0;
  let representativeSlice = 0;
  let representativePixels: number[][] = [];
  for (const [slice, pixels] of slices.sort(([a], [b]) => Number(a) - Number(b))) {
    let bounds: Bounds | undefined;
    const unique = new Set<string>();
    for (const [row, column] of pixels) {
      const key = `${row},${column}`;
      if (unique.has(key)) continue;
      unique.add(key);
      bounds = addPixel(bounds, row, column);
      voxelCount++;
    }
    if (bounds && (!largest || bounds.count > largest.count)) {
      largest = bounds;
      representativeSlice = Number(slice);
      representativePixels = pixels;
    }
  }
  const affine =
    grid?.origin?.length === 3 && grid.direction?.length === 9 && spacing?.length === 3
      ? [0, 1, 2].flatMap((axis) => [
          grid.direction![axis] * spacing[1],
          grid.direction![axis + 3] * spacing[0],
          grid.direction![axis + 6] * spacing[2],
          grid.origin![axis],
        ])
      : undefined;
  return {
    voxelCount,
    firstSliceIndex: Math.min(...slices.map(([index]) => Number(index))),
    lastSliceIndex: Math.max(...slices.map(([index]) => Number(index))),
    ...extent(largest, spacing),
    ...(grid && affine
      ? {
          volume: {
            rows: grid.rows,
            columns: grid.columns,
            affine,
            runs: pixelIndicesToRuns(
              slices.flatMap(([slice, pixels]) =>
                pixels.map(
                  ([row, column]) => row + grid.rows * (column + grid.columns * Number(slice)),
                ),
              ),
            ),
          },
        }
      : {}),
    ...(grid
      ? {
          representative: {
            sliceIndex: representativeSlice,
            rows: grid.rows,
            columns: grid.columns,
            affine,
            runs: pixelIndicesToRuns(
              representativePixels.map(([row, column]) => row * grid.columns + column),
            ),
          },
        }
      : {}),
  };
}

/** Count stored segment labels, never local renderer indices or rounded volume estimates. */
export function summarizeSavedMask(compressed: Uint8Array): Record<number, SavedCardGeometry> {
  const bytes = pako.inflate(compressed);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.byteLength < 352 || view.getInt32(0, true) !== 348)
    throw new Error("Invalid NIfTI mask header");
  const rows = view.getInt16(42, true);
  const columns = view.getInt16(44, true);
  const planeSize = rows * columns;
  const slices = view.getInt16(46, true);
  const datatype = view.getInt16(70, true);
  const size = ({ 2: 1, 4: 2, 512: 2, 16: 4 } as Record<number, number>)[datatype];
  const offset = Math.max(352, Math.round(view.getFloat32(108, true)));
  if (
    !size ||
    planeSize <= 0 ||
    slices <= 0 ||
    offset + planeSize * slices * size > view.byteLength
  ) {
    throw new Error("Unsupported or truncated NIfTI mask");
  }
  const result: Record<number, SavedCardGeometry> = {};
  const boundsBySegment = new Map<number, Map<number, Bounds>>();
  const volumeRuns = new Map<number, [number, number][]>();
  for (let index = 0; index < planeSize * slices; index++) {
    const position = offset + index * size;
    const label =
      datatype === 2
        ? view.getUint8(position)
        : datatype === 4
          ? view.getInt16(position, true)
          : datatype === 512
            ? view.getUint16(position, true)
            : view.getFloat32(position, true);
    if (!Number.isInteger(label) || label <= 0) continue;
    const runs = volumeRuns.get(label) ?? [];
    const previous = runs.at(-1);
    if (previous && previous[0] + previous[1] === index) previous[1]++;
    else runs.push([index, 1]);
    volumeRuns.set(label, runs);
    const slice = Math.floor(index / planeSize);
    const entry = (result[label] ??= {
      voxelCount: 0,
      firstSliceIndex: slice,
      lastSliceIndex: slice,
    });
    entry.voxelCount++;
    entry.lastSliceIndex = slice;
    const sliceBounds = boundsBySegment.get(label) ?? new Map<number, Bounds>();
    // Backend NIfTI arrays are (row, column, slice), written in Fortran order.
    sliceBounds.set(
      slice,
      addPixel(sliceBounds.get(slice), index % rows, Math.floor((index % planeSize) / rows)),
    );
    boundsBySegment.set(label, sliceBounds);
  }
  // The backend affine maps column first, then row (like vtkImageData).
  const spacing = [view.getFloat32(84, true), view.getFloat32(80, true)];
  const affine =
    view.getInt16(254, true) > 0
      ? Array.from({ length: 12 }, (_, index) => view.getFloat32(280 + index * 4, true))
      : undefined;
  for (const [label, sliceBounds] of boundsBySegment) {
    if (affine) result[label].volume = { rows, columns, affine, runs: volumeRuns.get(label)! };
    const [sliceIndex, largest] = [...sliceBounds.entries()].sort(
      (a, b) => b[1].count - a[1].count,
    )[0];
    Object.assign(result[label], extent(largest, spacing));
    const indices: number[] = [];
    for (let pixel = 0; pixel < planeSize; pixel++) {
      const position = offset + (sliceIndex * planeSize + pixel) * size;
      const value =
        datatype === 2
          ? view.getUint8(position)
          : datatype === 4
            ? view.getInt16(position, true)
            : datatype === 512
              ? view.getUint16(position, true)
              : view.getFloat32(position, true);
      if (value === label) indices.push((pixel % rows) * columns + Math.floor(pixel / rows));
    }
    result[label].representative = {
      sliceIndex,
      rows,
      columns,
      affine,
      runs: pixelIndicesToRuns(indices),
    };
  }
  return result;
}
